import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, type SubmitHandler } from "react-hook-form";
import { API_URL } from "../../services/api";
import InputField from "../../components/ui/InputField";
import Button from "../../components/ui/Button";
import { BsGoogle } from "react-icons/bs";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { useAuth } from "../../hooks/useAuth";

import Logo from "../../components/ui/Logo";

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  terms: boolean;
}
const PasswordRequirements = ({ password }: { password: string }) => {
  const requirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'One lowercase letter', met: /[a-z]/.test(password) },
    { text: 'One number', met: /[0-9]/.test(password) },
    { text: 'One special character', met: /[!@#$%^&*]/.test(password) }
  ];

  return (
    <div className="mt-2 text-xs">
      <p className="text-gray-600 mb-1">Password requirements:</p>
      {requirements.map((req, index) => (
        <div key={index} className={`flex items-center ${req.met ? 'text-green-600' :'text-red-600'}`}>
          <span className="mr-1 text-xs">{req.met ? '✓' : '×'}</span>
          <span className="text-xs">{req.text}</span>
        </div>
      ))}
    </div>
  );
};
export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordReqs, setShowPasswordReqs] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register, handleSubmit, formState: { errors }, setError, watch } = useForm<SignupFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      terms: false,
    },
  });
const password = watch("password"); 

const validatePassword = (value: string) => {
  if (value.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(value)) return "Password must contain an uppercase letter";
  if (!/[a-z]/.test(value)) return "Password must contain a lowercase letter";
  if (!/[0-9]/.test(value)) return "Password must contain a number";
  if (!/[!@#$%^&*]/.test(value)) return "Password must contain a special character";
  return true;
};
  const onSubmit: SubmitHandler<SignupFormData> = async (data) => {
    if (!data.terms) {
      setError("terms", { message: "You must agree to the terms and conditions" });
      return;
    }

    setIsLoading(true);
    try {
      await signup({
        firstName: data.firstName,
        lastName: data.lastName,


        email: data.email,
        password: data.password,
      });
      navigate("/dashboard");
    } catch (error) {
      setError("root", {
        message: error instanceof Error ? error.message : "An error occurred during signup",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-[#F7D4E9] to-[#E2E6FF] px-4 py-8 grid place-items-center">
      <div className="w-full max-w-6xl mx-auto flex flex-col md:flex-row gap-8 px-4 md:px-6 md:h-[95%]">
        <div className="w-full md:w-1/2 h-[200px] md:h-auto rounded-xl shadow-lg border border-white/20 overflow-hidden bg-white/10 backdrop-blur-xs">
          <img src="/images/auth-hero.png" alt="Hero" className="w-full h-full object-cover" />
        </div>
        <div className="w-full md:w-1/2 max-w-lg flex flex-col justify-center space-y-6 mx-auto md:mx-0">
          <Logo />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Create an account</h1>
            <p className="text-gray-500 mt-1.5 text-sm">Enter your credentials to access your account</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                type="text"
                placeholder="First name"
                variant="outline"
                disabled={isLoading}
                error={errors.firstName?.message}
                {...register("firstName", {
                required: "First name is required",
                minLength: { value: 2, message: "First name must be at least 2 characters" },
                maxLength: { value: 30, message: "First name must not exceed 30 characters" }
                })}
                className="focus:border-purple-600 bg-transparent!"
              />
              <InputField
                type="text"
                placeholder="Last name"
                variant="outline"
                disabled={isLoading}
                error={errors.lastName?.message}
                {...register("lastName", {
                required: "Last name is required", 
                minLength: { value: 2, message: "Last name must be at least 2 characters" },
                maxLength: { value: 30, message: "Last name must not exceed 30 characters" }
                })}
                className="focus:border-purple-600 bg-transparent!"
              />


            </div>

            <InputField
              type="email"
              placeholder="Email"
              variant="outline"
              disabled={isLoading}
              error={errors.email?.message}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email",
                },
              })}
              className="focus:border-purple-600 bg-transparent!"
            />

            <div>
              <div className="relative">
                <InputField
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password..."
                  variant="outline"
                  disabled={isLoading}
                  error={errors.password?.message}
                  {...register("password", {
                    required: "Password is required",
                    validate: validatePassword
                  })}
                  className="focus:border-purple-600 bg-transparent! pr-11"
                  onFocus={() => setShowPasswordReqs(true)}
                  onBlur={() => setShowPasswordReqs(false)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[11px] text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  tabIndex={-1}
                >
                  {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                </button>
              </div>
              {showPasswordReqs && password && <PasswordRequirements password={password} />}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="terms"
                className="w-4 h-4 text-purple-600 border-gray-300 rounded cursor-pointer"
                {...register("terms", { required: true })}
              />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I agree to the{" "}
                <a href="#" className="text-blue-600 hover:underline">
                  Terms & Conditions
                </a>
              </label>
            </div>
            {errors.terms && (
              <p className="text-red-600 text-sm">{errors.terms.message}</p>
            )}

            {errors.root && (
              <p className="text-red-600 text-sm">{errors.root.message}</p>
            )}

            <Button
              type="submit"
              variant

="primary"
              size="full"
              disabled={isLoading}
              className="mt-2"
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <div className="relative max-w-md mx-auto w-full">
            <hr className="my-3 border-gray-300" />
            <p className="absolute top-0.5 left-1/2 -translate-x-1/2 bg-[#E5E0FF] px-3 text-center text-sm text-gray-500">or register with</p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full bg-white flex items-center justify-center gap-3"
            onClick={() => {
              window.location.href = `${API_URL}/api/auth/google`;
            }}
          >
            <BsGoogle className="text-lg" />
            <span>Google</span>
          </Button>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <a
              href="/login"
              className="text-purple-600 hover

:underline font-semibold"
            >
              Log in
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}